<template>
    <div id="fiche-bu-monthly-frequency-rate-graph" class="background-quaternary-lighter">
        <div class="background-quaternary flex text-center">
            <div class="col-12">
                <as-title class="color-white" :title="$t('ficheBu.detail.monthly_frequency_rate_graph.title')"
                    :size="4"></as-title>
            </div>
        </div>

        <div class="flex" style="height: 323px;">
            <ProgressBar v-if="isBusy" mode="indeterminate" style="height: 3px; max-width: 75%;" class="flex-1 m-auto">
            </ProgressBar>
            <div v-else-if="!graphMonthlyData || graphMonthlyData.length === 0"
                class="w-100 h-100 flex flex-column align-items-center text-center">
                <as-title class="mt-auto" :title="$t('ficheBu.detail.no_data_message')" :size="6"></as-title>
                <img :src="noDataImage" class="ml-auto mr-auto mb-auto" />
            </div>
            <Bar v-else class="p-1" :data="chartData" :options="chartOptions" style="height: 100%; width: 100%;" />
        </div>
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarController,
    LineController
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ProgressBar from "primevue/progressbar";
import AsTitle from '../Elements/AsTitle.vue';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    BarController,
    LineController,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
)


export default {
    name: "FicheBuMonthlyFrequencyRateGraph",
    components: { AsTitle, Bar, ProgressBar },
    props: {
        graphMonthlyData: {
            type: Array,
        },
        isBusy: Boolean
    },
    data() {
        return {
            noDataImage: new URL("../../assets/images/icons/no-data-warning.png", import.meta.url).href,
        };
    },
    computed: {
        chartData() {
            if (!this.graphMonthlyData) {
                return {
                    labels: [],
                    datasets: []
                }
            }

            const midDataLength = this.graphMonthlyData.length / 2
            const currentMonthlyData = this.graphMonthlyData.slice(midDataLength)
            const labels = currentMonthlyData.map(d => {
                const month = String(d.month).padStart(2, '0')
                return `${d.year}-${month}`
            })

            const currentMonthlyValues = currentMonthlyData.map(d => Number(d.TF.toFixed(2)))
            const avgMonthlyValues = currentMonthlyData.map(d => Number(d.TF12AVG.toFixed(2)))

            return {
                labels,
                datasets: [
                    {
                        type: 'line',
                        label: '12 month cumulative',
                        data: avgMonthlyValues,
                        backgroundColor: 'rgb(36, 79, 95)',
                    },
                    {
                        type: 'bar',
                        label: 'Monthly',
                        data: currentMonthlyValues,
                        backgroundColor: '#84a7b7',
                        borderRadius: 4,
                    },
                ]
            }
        },
        chartOptions() {
            return {
                responsive: true,
                layout: {
                    padding: {
                        top: 20,
                        right: 10,
                        left: 10,
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            boxWidth: 30,
                            generateLabels: (chart) => {
                                const datasets = chart.data.datasets || []
                                return datasets.map((ds, i) => {
                                    const isLine = ds.type === 'line'
                                    const hidden = !chart.isDatasetVisible(i)
                                    return {
                                        text: ds.label || `Dataset ${i}`,
                                        fillStyle: ds.backgroundColor,
                                        strokeStyle: ds.borderColor || ds.backgroundColor,
                                        hidden,
                                        datasetIndex: i,
                                        pointStyle: isLine ? 'line' : 'circle',
                                        lineWidth: isLine ? (ds.borderWidth || 2) : 0
                                    }
                                })
                            }
                        }
                    },
                    datalabels: {
                        display: (context) => context.dataset.type === 'line',
                        align: 'top',
                        anchor: 'end',
                        formatter: (value) => value,
                        font: {
                            size: 12,
                            weight: 'bold'
                        },
                        color: "rgb(36, 79, 95)"
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: (ctx) => {
                            const max = Math.max(...ctx.chart.data.datasets.flatMap(ds => ds.data))
                            return max * 1.05
                        }
                    }
                }
            }
        }
    },
};
</script>

<style lang="scss">
@import "../../assets/styles/colors.scss";
</style>
